import { ExcelController } from "@controllers/Excel.controller";
import { type Association, type Opportunity, OpportunityParticipant } from "@entities";
import { Message01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { CircularProgress, Stack } from "@mui/material";
import { opportunityParticipantRepo } from "@repositories";
import { linko } from "@shared/linko";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { type QueryObserverResult, type RefetchOptions } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import { UserCardPopup } from "../../../components/popups/user-card/UserCardPopup";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useUser } from "../../../contexts/UserProvider";
import { useAlert } from "../../../contexts/AlertProvider";
import { downloadExcelFile } from "../../../helpers/excel/export";
import {
  ADMIN_BULK_ACTION_LIMIT,
  runInChunks,
} from "../../../helpers/admin-list-query";
import { useUpdateEventParticipant } from "../../../queries/events.query";
import { useOpportunityParticipantColumns } from "./columns";
import filters from "./filters";
import {
  SendEventParticipantsWhatsappDialog,
  type WhatsappDialogRecipient,
} from "./SendEventParticipantsWhatsappDialog";

const ParticipantsTable: FC<{
  id: string;
  refetchEvent: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<Opportunity | null | undefined, Error>>;
  opportunity?: Opportunity;
  /** במשימה קבוצתית - רק מתנדבים שנרשמו בודד (ללא groupId) */
  individualsOnly?: boolean;
  tableId?: string;
}> = ({ id, refetchEvent, opportunity, individualsOnly = false, tableId }) => {
  const { HyperTable } = useCreateHyperTable(OpportunityParticipant);
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const isCallCenterStation = !!currentUser?.isCallCenterStation;
  const { setAlertText, setSeverity } = useAlert();
  const [currentUserRowData, setCurrentUserRowData] = useState<Association>();
  const { mutateAsync: removeParticipants } = useUpdateEventParticipant();
  const columns = useOpportunityParticipantColumns(HyperTable);
  const [whatsappRecipients, setWhatsappRecipients] = useState<
    WhatsappDialogRecipient[]
  >([]);
  const [whatsappSending, setWhatsappSending] = useState(false);

  useEffect(() => {
    HyperTable.context.current?.refetch("remove");
  }, []);

  const buttons: ActionButton<OpportunityParticipant>[] = useMemo(() => {
    return [
      {
        icon: (
          <HugeiconsIcon icon={Message01Icon} size={22} strokeWidth={2} />
        ),
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;
          const participants = await opportunityParticipantRepo.find({
            where: whereToUpdate,
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
            include: { association: { include: { user: true } } },
          });
          const toProcess = participants.slice(0, ADMIN_BULK_ACTION_LIMIT);
          const seen = new Set<string>();
          const recipients: WhatsappDialogRecipient[] = [];
          for (const row of toProcess) {
            const userId = row.association?.userId;
            const user = row.association?.user;
            if (!userId || seen.has(userId)) continue;
            seen.add(userId);
            recipients.push({
              userId,
              name: user?.name,
              firstName: user?.firstName,
              phoneNumber: user?.phoneNumber,
            });
          }
          if (recipients.length === 0) {
            setAlertText("לא נבחרו מתנדבים לשליחה");
            setSeverity("warning");
            return;
          }
          setWhatsappRecipients(recipients);
          if (participants.length > ADMIN_BULK_ACTION_LIMIT) {
            setAlertText(
              `יישלח עד ${ADMIN_BULK_ACTION_LIMIT} מתנדבים. חזרו על הפעולה לשאר.`,
            );
            setSeverity("warning");
          }
          return { refetch: false, resetSelection: false };
        },
        title: "שליחת וואטסאפ",
      },
      {
        icon: <PersonRemoveIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          // Get all participants matching the filter criteria
          const participants = await opportunityParticipantRepo.find({
            where: whereToUpdate,
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });
          const toProcess = participants.slice(0, ADMIN_BULK_ACTION_LIMIT);
          await runInChunks(toProcess, 8, async (row) => {
            await removeParticipants({
              opportunityParticipantId: row.id,
              opportunityId: id,
              showSuccessAlert: false,
            });
          });
          const truncated = participants.length > ADMIN_BULK_ACTION_LIMIT;
          setAlertText(
            truncated
              ? `הוסרו עד ${ADMIN_BULK_ACTION_LIMIT} משתתפים. חזרו על הפעולה לשאר.`
              : toProcess.length === 1
                ? "המשתתף הוסר בהצלחה"
                : `${toProcess.length} משתתפים הוסרו בהצלחה`,
          );
          setSeverity("success");
          refetchEvent();
          HyperTable.context.current?.refetch("remove");
        },
        title: "הסרה ממשימה",
      },
      {
        icon: <FileUploadIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          const excelResult =
            await ExcelController.exportParticipateInOpportunitiesToExcel(
              whereToUpdate,
            );
          if (excelResult) {
            downloadExcelFile(excelResult);
          }

          return {
            refetch: false,
            resetSelection: true,
          };
        },
        title: "ייצוא לאקסל",
      },
    ];
  }, [id, removeParticipants, setAlertText, setSeverity, HyperTable, refetchEvent]);
  const visibleButtons = isCallCenterStation
    ? buttons.filter((button) => button.title !== "שליחת וואטסאפ")
    : buttons;

  const sendWhatsapp = async (
    templateKey: string,
    customFields: Record<string, string> | undefined,
    confirmedRecentUserIds: string[],
  ) => {
    setWhatsappSending(true);
    try {
      const { data: result } =
        await linko.opportunity.mutations.sendEventParticipantsWhatsapp({
          opportunityId: id,
          userIds: whatsappRecipients.map((recipient) => recipient.userId),
          templateKey,
          customFields,
          confirmedRecentUserIds,
        });
      setAlertText(result.message);
      setSeverity(result.failed === 0 && result.skipped === 0 ? "success" : "warning");
      setWhatsappRecipients([]);
    } catch (error: unknown) {
      setAlertText(
        isAxiosTypedLinkoError(error)
          ? error.linkoError.message
          : "שליחת הוואטסאפ נכשלה",
      );
      setSeverity("error");
    } finally {
      setWhatsappSending(false);
    }
  };

  useEffect(() => {
    HyperTable.context.current?.refetch("remove");
  }, []);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  const resolvedTableId =
    tableId ??
    (individualsOnly ? `participants-individuals-${id}` : `participants-table-${id}`);

  return (
    <HyperTable
      tableId={resolvedTableId}
      findConfig={{
        where: individualsOnly
          ? {
              $and: [
                {
                  opportunity: { $id: id },
                  status: "APPROVED",
                },
                OpportunityParticipant.isIndividualParticipant(),
              ],
            }
          : {
              opportunity: { $id: id },
              status: "APPROVED",
            },
        include: {
          association: { include: { user: true, teams: true } },
        },
      }}
      onRowClick={(_, row) => setCurrentUserRowData(row.original.association)}
      title={null}
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {columns.map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <HyperTable.SelectColumn />
      <UserCardPopup
        userData={currentUserRowData}
        setUserData={() => setCurrentUserRowData(undefined)}
        isAdd={false}
        handleSave={() => {
          HyperTable.context.current?.refetch("remove");
        }}
        setIsAdd={() => { }}
      />
      <HyperTable.ActionsBar buttons={visibleButtons} />
      <SendEventParticipantsWhatsappDialog
        open={whatsappRecipients.length > 0}
        onClose={() => {
          if (!whatsappSending) setWhatsappRecipients([]);
        }}
        sending={whatsappSending}
        recipientCount={whatsappRecipients.length}
        recipients={whatsappRecipients}
        opportunityId={id}
        opportunity={opportunity}
        onSend={sendWhatsapp}
      />
    </HyperTable>
  );
};

export default ParticipantsTable;
